import {getAuth, type User} from 'firebase/auth';
import {getUserRoleRef} from '../../firebase/models/User';
import {useState} from 'react';
import {getDoc, onSnapshot} from 'firebase/firestore';
import {Role} from 'types/role';
import {useEffect} from 'react';
import type {CourseId, UserId} from 'types/common';
import {firebaseStringToRole} from 'contexts/profileUtil';

/**
 * Returns the current user's uid from the Firebase auth singleton.
 *
 * Non-reactive: this is a plain synchronous read of `auth.currentUser`, safe
 * to call from anywhere (event handlers, async callbacks, logging utils).
 * Components that need to re-render on sign-in/sign-out should subscribe to
 * the auth state separately rather than relying on this helper.
 *
 * @return The signed-in user's uid, or `null` if no user is signed in or the
 *   auth SDK threw while resolving the singleton.
 */
export function getUserId(): UserId | null {
  try {
    const auth = getAuth();

    // be really careful!
    return auth?.currentUser?.uid ?? null;
  } catch (e) {
    console.trace('Error in getUserId', e);
    return null;
  }
}

/**
 * Reads the current user's uid from the Firebase auth singleton.
 *
 * Despite the `use` prefix, this is NOT a React hook: it returns synchronously
 * and does not subscribe to auth-state changes, so React will not re-render
 * when the user signs in or out. Prefer {@link getUserId} in new code; this
 * function is retained only for backwards compatibility with existing
 * callers.
 *
 * @return The signed-in user's uid, or `null` if no user is signed in or the
 *   auth SDK threw while resolving the singleton.
 * @deprecated Misleadingly named — use {@link getUserId} instead.
 */
// eslint-disable-next-line @eslint-react/no-unnecessary-use-prefix -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
export function useUserId(): UserId | null {
  return getUserId();
}

export function getAuthUser(): User | null {
  try {
    const auth = getAuth();
    return auth?.currentUser ?? null;
  } catch (e) {
    console.trace('Error in useUser', e);
    return null;
  }
}

/**
 * Reactively tracks the signed-in user's uid, re-rendering the caller on
 * sign-in/sign-out — unlike {@link getUserId}, a one-time, non-reactive
 * read. For a component that must know precisely when auth finishes
 * restoring a session (e.g. gating a Firestore subscription that a deep
 * link can otherwise mount before the SDK settles).
 *
 * @return The signed-in user's uid, or `null` if signed out. Initialized
 *     from {@link getUserId} synchronously so an already-warm session
 *     doesn't flash "signed out" for one render before the listener fires.
 * @throws Never.
 */
export function useAuthUid(): UserId | null {
  const [uid, setUid] = useState<UserId | null>(getUserId);
  useEffect(() => {
    const unsubscribe = getAuth().onAuthStateChanged(user => {
      setUid(user?.uid ?? null);
    });
    return () => {
      unsubscribe();
    };
  }, []);
  return uid;
}

interface UseUserRoleParams {
  userId: UserId;
  courseId: CourseId;
}

interface UseUserRoleResult {
  userRole: Role;
  isLoading: boolean;
  error: Error | null;
}
export function useUserRole({
  userId,
  courseId,
}: UseUserRoleParams): UseUserRoleResult {
  const [userRole, setUserRole] = useState<Role>(Role.UNREGISTERED);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    if (!userId || !courseId) {
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      setIsLoading(false);
      return;
    }
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    setIsLoading(true);
    const userRoleRef = getUserRoleRef(userId, courseId);
    const unsubscribe = onSnapshot(
      userRoleRef,
      doc => {
        setUserRole(firebaseStringToRole(doc.data()));
        setIsLoading(false);
      },
      error => {
        setError(error);
        setIsLoading(false);
      },
    );
    return () => unsubscribe();
  }, [userId, courseId]);
  return {userRole, isLoading, error};
}

/**
 * Non-reactive function to get the user role. Might be stale.
 */
export const getUserRole = async ({
  userId,
  courseId,
}: UseUserRoleParams): Promise<Role> => {
  const userRoleRef = getUserRoleRef(userId, courseId);
  const doc = await getDoc(userRoleRef);
  return firebaseStringToRole(doc.data() ?? 'unregistered') ;
};
